import * as React from "react";
import { cn } from "../lib/cn";

/**
 * Subset of `@ando/core/domain.SourceType` — duplicated here as a string
 * literal union so `@ando/ui` stays decoupled from `@ando/core` (UI is a
 * leaf package, must not depend on domain layers). Keep in sync with
 * `packages/core/src/domain/source.ts`.
 */
export type MessageCitationSourceType =
  | "document"
  | "email"
  | "chat"
  | "database"
  | "web"
  | "other";

export interface MessageCitationProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> {
  /** 1-based citation index as rendered inline (`[1]`, `[2]`, …). */
  index: number;
  /** Source id — resolves to a Source via the chat slice's source map. */
  sourceId: string;
  /**
   * Optional source-type hint. Lets the click handler skip a lookup when
   * routing the open action (`document` → SourceDrawer, `web` → external,
   * `database` → enterprise drawer, …).
   */
  sourceType?: MessageCitationSourceType;
  /** Optional title for the tooltip / aria-label. Defaults to "Source N". */
  title?: string;
  /**
   * Inline metadata the chat already has about the citation —
   * forwarded verbatim to ``onCitationClick`` so the click handler
   * can short-circuit the BE round-trip (e.g. ``web`` sources carry
   * the full URL inline and don't need a resolver call). Jun 2026.
   */
  url?: string;
  citationTitle?: string;
  snippet?: string;
  publishedDate?: string;
  /**
   * Click handler — the parent maps `sourceId` + `sourceType` to the
   * correct drawer (A.11 SourceDrawer for documents, enterprise drawer for
   * `database`, ``WebPreview`` for `web`, …). ``hints`` carries the
   * inline metadata from the chat citation when available so the parent
   * can skip the BE resolver for web sources.
   */
  onCitationClick?: (
    sourceId: string,
    sourceType?: MessageCitationSourceType,
    hints?: {
      url?: string;
      title?: string;
      snippet?: string;
      publishedDate?: string;
    },
  ) => void;
}

/**
 * MessageCitation — inline numbered pill `[1]`.
 *
 * Rendered inline inside `MessageBody` markdown after parsing the
 * `[<n>]` shorthand. Visual: mono small caps, blue/grey base, orange-soft on
 * hover, scales to 0.95 on press. Always a `<button>` — never a span — so
 * keyboard navigation works.
 *
 * Contract:
 *   ```tsx
 *   <MessageCitation
 *     index={1}
 *     sourceId="src-abc"
 *     sourceType="document"
 *     onCitationClick={(id, type) => openSourceDrawer(id, type)}
 *   />
 *   ```
 *
 * Wire-up: feature consumers in `apps/client/src/features/chat` pass the
 * drawer-open callback. The component itself is presentation-only.
 */
export const MessageCitation = React.forwardRef<HTMLButtonElement, MessageCitationProps>(
  (
    {
      index,
      sourceId,
      sourceType,
      url,
      citationTitle,
      snippet,
      publishedDate,
      onCitationClick,
      className,
      title,
      type,
      ...rest
    },
    ref,
  ) => {
    const label = title ?? `Apri fonte ${index}`;
    return (
      <button
        ref={ref}
        type={type ?? "button"}
        onClick={() =>
          onCitationClick?.(sourceId, sourceType, {
            url,
            title: citationTitle,
            snippet,
            publishedDate,
          })
        }
        aria-label={label}
        title={label}
        data-citation-id={sourceId}
        data-citation-type={sourceType}
        className={cn(
          "mx-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full",
          "bg-ando-orange-soft px-1.5 align-baseline font-mono font-semibold text-[10px] text-ando-orange",
          "transition-[transform,background-color,color] duration-fast ease-smooth",
          "hover:bg-ando-orange hover:text-white hover:shadow-xs",
          "active:scale-95",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1 focus-visible:ring-offset-cream",
          className,
        )}
        {...rest}
      >
        {index}
      </button>
    );
  },
);
MessageCitation.displayName = "MessageCitation";
